import { useMemo } from "react";
import type {
  SchrijvenFormField,
  SchrijvenFormSchema,
  SchrijvenVariantContext,
} from "@a2/core";
import { useT } from "../../lib/use-t";

/** Draft save-state pill — the draft auto-persists to localStorage. */
function SavePill() {
  const t = useT();
  return (
    <span className="inline-flex items-center gap-1.5 font-mono-badge text-xs text-[var(--ink-faint)]">
      <span className="inline-block w-[7px] h-[7px] rounded-full bg-[var(--pass)]" aria-hidden />
      {t("schrijven.runner.saved")}
    </span>
  );
}

interface FormShellProps {
  variant: SchrijvenVariantContext;
  value: string;
  onChange: (text: string) => void;
  disabled?: boolean;
}

/**
 * Form composer with two modes:
 *
 * 1. **Schema mode** — when the variant carries a formSchema, render exactly
 *    those sections and fields. Mirrors how real DUO formulieren are laid
 *    out (persoonsgegevens block + a couple of open-ended question sections).
 *    Section titles appear as gray headers; line fields render as single-line
 *    inputs labeled to the left; area fields render as textareas under the
 *    section title.
 *
 * 2. **Bullet fallback** — when formSchema is absent, fall back to
 *    one-textarea-per-bullet so legacy variants still work.
 *
 * In both modes `value` is the source of truth (parsed on every render).
 * That keeps session-resume snapshots and external resets working without
 * extra state plumbing.
 */
export function FormShell({
  variant,
  value,
  onChange,
  disabled = false,
}: FormShellProps) {
  if (variant.formSchema) {
    return (
      <SchemaForm
        schema={variant.formSchema}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }
  return (
    <BulletForm
      bullets={variant.bullets}
      value={value}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

// ---------------------------------------------------------------------------
// Schema-driven rendering
// ---------------------------------------------------------------------------

function SchemaForm({
  schema,
  value,
  onChange,
  disabled,
}: {
  schema: SchrijvenFormSchema;
  value: string;
  onChange: (text: string) => void;
  disabled: boolean;
}) {
  const fields = useMemo(() => parseFormValue(value, schema), [value, schema]);
  const totalFields = schema.sections.reduce((sum, s) => sum + s.fields.length, 0);
  const filled = fields.reduce(
    (sum, sec) => sum + sec.filter((f) => f.trim().length > 0).length,
    0,
  );

  function updateField(sectionIdx: number, fieldIdx: number, text: string) {
    const next = fields.map((arr) => [...arr]);
    const section = next[sectionIdx];
    if (!section) return;
    section[fieldIdx] = text;
    onChange(serializeFormValue(next, schema));
  }

  return (
    <article className="flex flex-col min-h-0 flex-1 rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel)] shadow-[var(--shadow)] overflow-hidden">
      <header className="px-5 py-3 border-b border-[var(--border)] bg-[var(--panel-soft)] flex items-center gap-3">
        <h2 className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
          Formulier
        </h2>
        <span className="font-mono-badge text-[10px] uppercase tracking-wider text-[var(--ink-faint)]">
          {filled} / {totalFields} ingevuld
        </span>
        <span className="ml-auto">
          <SavePill />
        </span>
      </header>

      <div className="flex-1 min-h-0 overflow-auto">
        {schema.sections.map((section, si) => (
          <section key={si} className="border-t border-[var(--border)] first-of-type:border-t-0">
            <h3 className="px-5 py-2 bg-[var(--panel-soft)] border-b border-[var(--border)] text-sm font-semibold text-[var(--ink)]">
              {section.title}
            </h3>
            <div className="divide-y divide-[var(--border)]">
              {section.fields.map((field, fi) => (
                <SchemaFieldRow
                  key={fi}
                  field={field}
                  value={fields[si]?.[fi] ?? ""}
                  disabled={disabled}
                  onChange={(t) => updateField(si, fi, t)}
                  id={`form-s${si}-f${fi}`}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </article>
  );
}

function SchemaFieldRow({
  field,
  value,
  onChange,
  disabled,
  id,
}: {
  field: SchrijvenFormField;
  value: string;
  onChange: (t: string) => void;
  disabled: boolean;
  id: string;
}) {
  switch (field.kind) {
    case "line":
      return (
        <LineFieldRow field={field} value={value} onChange={onChange} disabled={disabled} id={id} narrow={false} />
      );
    case "text-short":
      return (
        <LineFieldRow field={field} value={value} onChange={onChange} disabled={disabled} id={id} narrow />
      );
    case "radio":
      return (
        <RadioFieldRow field={field} value={value} onChange={onChange} disabled={disabled} id={id} />
      );
    case "enum":
      return (
        <EnumFieldRow field={field} value={value} onChange={onChange} disabled={disabled} id={id} />
      );
    case "area":
    case "text-block":
      return (
        <AreaFieldRow
          field={field}
          value={value}
          onChange={onChange}
          disabled={disabled}
          id={id}
        />
      );
  }
}

function LineFieldRow({
  field,
  value,
  onChange,
  disabled,
  id,
  narrow,
}: {
  field: SchrijvenFormField;
  value: string;
  onChange: (t: string) => void;
  disabled: boolean;
  id: string;
  narrow: boolean;
}) {
  return (
    <div className="px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
      {field.label ? (
        <label
          htmlFor={id}
          className="sm:w-44 sm:shrink-0 text-sm text-[var(--ink-muted)]"
        >
          {field.label}
        </label>
      ) : null}
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={
          (narrow ? "sm:max-w-[14rem] " : "flex-1 ") +
          "rounded-md bg-[var(--bg)] border border-[var(--border)] px-3 py-2 text-sm text-[var(--ink)] placeholder:text-[var(--ink-faint)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2 disabled:opacity-50"
        }
      />
    </div>
  );
}

function AreaFieldRow({
  field,
  value,
  onChange,
  disabled,
  id,
}: {
  field: SchrijvenFormField;
  value: string;
  onChange: (t: string) => void;
  disabled: boolean;
  id: string;
}) {
  return (
    <div className="px-5 py-3 space-y-2">
      {field.label ? (
        <label htmlFor={id} className="block text-sm text-[var(--ink-muted)]">
          {field.label}
        </label>
      ) : null}
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        rows={4}
        placeholder="Schrijf hier…"
        className="w-full rounded-md bg-[var(--bg)] border border-[var(--border)] px-3 py-2 text-sm text-[var(--ink)] leading-relaxed placeholder:text-[var(--ink-faint)] resize-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2 disabled:opacity-50"
      />
      {field.kind === "text-block" && field.minSentences ? (
        <p className="font-mono-badge text-[10px] uppercase tracking-wider text-[var(--ink-faint)]">
          Schrijf minimaal {field.minSentences} zinnen.
        </p>
      ) : null}
    </div>
  );
}

function RadioFieldRow({
  field,
  value,
  onChange,
  disabled,
  id,
}: {
  field: SchrijvenFormField;
  value: string;
  onChange: (t: string) => void;
  disabled: boolean;
  id: string;
}) {
  const options = field.options ?? ["Ja", "Nee"];
  return (
    <div className="px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
      {field.label ? (
        <span className="sm:w-44 sm:shrink-0 text-sm text-[var(--ink-muted)]">
          {field.label}
        </span>
      ) : null}
      <div role="radiogroup" aria-labelledby={id} className="flex gap-3 flex-wrap">
        {options.map((opt) => {
          const selected = value === opt;
          return (
            <label
              key={opt}
              className={
                "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm cursor-pointer transition-colors " +
                (selected
                  ? "border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--ink)]"
                  : "border-[var(--border)] bg-[var(--bg)] text-[var(--ink-muted)] hover:text-[var(--ink)]")
              }
            >
              <input
                type="radio"
                name={id}
                value={opt}
                checked={selected}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                className="sr-only"
              />
              {opt}
            </label>
          );
        })}
      </div>
    </div>
  );
}

function EnumFieldRow({
  field,
  value,
  onChange,
  disabled,
  id,
}: {
  field: SchrijvenFormField;
  value: string;
  onChange: (t: string) => void;
  disabled: boolean;
  id: string;
}) {
  const options = field.options ?? [];
  return (
    <div className="px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
      {field.label ? (
        <label
          htmlFor={id}
          className="sm:w-44 sm:shrink-0 text-sm text-[var(--ink-muted)]"
        >
          {field.label}
        </label>
      ) : null}
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="flex-1 rounded-md bg-[var(--bg)] border border-[var(--border)] px-3 py-2 text-sm text-[var(--ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2 disabled:opacity-50"
      >
        <option value="">— Kies —</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Schema parse + serialise — round-trip stable
// ---------------------------------------------------------------------------

/**
 * Serialised value shape (used by the grader and round-tripped via this
 * parser):
 *
 *   ## Persoonsgegevens
 *   Voor- en achternaam: Eren Yıldırım
 *   Adres: Hoofdstraat 12
 *   …
 *
 *   ## In welke straat zijn er problemen?
 *   Bospad
 *
 *   ## Wat zijn de problemen?
 *   Multiple lines of body text
 *   can live here just fine.
 *
 * Section blocks are separated by a blank line. Single-line fields live as
 * "Label: value" lines. Area fields (and labelless line fields) take the
 * whole content of their section.
 */
export function parseFormValue(value: string, schema: SchrijvenFormSchema): string[][] {
  const result: string[][] = schema.sections.map((s) => s.fields.map(() => ""));
  if (!value || !value.trim()) return result;

  // Split into section blocks by the "## " line marker.
  const blocks = value.split(/\n*##\s+/);
  for (const block of blocks) {
    if (!block.trim()) continue;
    const lineEnd = block.indexOf("\n");
    const title = (lineEnd >= 0 ? block.slice(0, lineEnd) : block).trim();
    const content = (lineEnd >= 0 ? block.slice(lineEnd + 1) : "").trim();

    const sectionIdx = schema.sections.findIndex((s) => s.title === title);
    if (sectionIdx < 0) continue;
    const section = schema.sections[sectionIdx];
    if (!section) continue;

    if (section.fields.length === 1 || section.fields.every((f) => f.kind === "area")) {
      // Single field — assign all of content to field 0. Do NOT trim:
      // the user might mid-type a space and we'd otherwise eat it on the
      // next re-render.
      const targetSection = result[sectionIdx];
      if (targetSection) targetSection[0] = stripLeadingNewlines(content);
      continue;
    }

    // Multiple line fields: match by label prefix, falling back to position.
    // Preserve user whitespace — only strip the *leading* single space that
    // follows the "Label: " convention. Never trim trailing whitespace or
    // mid-string spaces, otherwise a keystroke-time round-trip eats the
    // space the user just pressed.
    const lines = content.split("\n").filter((l) => l.length > 0);
    const usedLineIdx = new Set<number>();
    for (let fi = 0; fi < section.fields.length; fi++) {
      const field = section.fields[fi];
      if (!field) continue;
      let matched: string | null = null;
      if (field.label) {
        const labelPrefix = field.label.toLowerCase() + ":";
        const idx = lines.findIndex(
          (l, i) => !usedLineIdx.has(i) && l.toLowerCase().startsWith(labelPrefix),
        );
        if (idx >= 0) {
          const raw = lines[idx] ?? "";
          matched = stripLeadingSpace(raw.slice(labelPrefix.length));
          usedLineIdx.add(idx);
        }
      }
      if (matched === null) {
        // Positional fallback: the i-th unused line.
        const idx = lines.findIndex((_, i) => !usedLineIdx.has(i));
        if (idx >= 0) {
          const raw = lines[idx] ?? "";
          matched = raw.includes(":")
            ? stripLeadingSpace(raw.slice(raw.indexOf(":") + 1))
            : raw;
          usedLineIdx.add(idx);
        }
      }
      const targetSection = result[sectionIdx];
      if (targetSection) targetSection[fi] = matched ?? "";
    }
  }
  return result;
}

function stripLeadingSpace(s: string): string {
  return s.startsWith(" ") ? s.slice(1) : s;
}

function stripLeadingNewlines(s: string): string {
  return s.replace(/^\n+/, "");
}

export function serializeFormValue(
  fields: string[][],
  schema: SchrijvenFormSchema,
): string {
  // Preserve user whitespace verbatim — no .trim() on field values, or we
  // eat the space the user is mid-typing on the next re-render.
  return schema.sections
    .map((section, si) => {
      const sectionFields = fields[si] ?? [];
      const body = section.fields
        .map((field, fi) => {
          const v = sectionFields[fi] ?? "";
          if (!field.label) return v;
          return `${field.label}: ${v}`;
        })
        .join("\n");
      return `## ${section.title}\n${body}`;
    })
    .join("\n\n");
}

// ---------------------------------------------------------------------------
// Bullet fallback (used when variant.formSchema is null)
// ---------------------------------------------------------------------------

function BulletForm({
  bullets,
  value,
  onChange,
  disabled,
}: {
  bullets: string[];
  value: string;
  onChange: (text: string) => void;
  disabled: boolean;
}) {
  const fields = useMemo(() => parseBulletFields(value, bullets.length), [value, bullets.length]);
  const filledCount = fields.filter((f) => f.trim().length > 0).length;

  function update(idx: number, text: string) {
    const next = [...fields];
    next[idx] = text;
    onChange(serialiseBulletFields(next));
  }

  return (
    <article className="flex flex-col min-h-0 flex-1 rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel)] shadow-[var(--shadow)] overflow-hidden">
      <header className="px-5 py-3 border-b border-[var(--border)] bg-[var(--panel-soft)] flex items-center gap-3">
        <h2 className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
          Formulier
        </h2>
        <span className="font-mono-badge text-[10px] uppercase tracking-wider text-[var(--ink-faint)]">
          {filledCount} / {bullets.length} ingevuld
        </span>
        <span className="ml-auto">
          <SavePill />
        </span>
      </header>

      <ol className="flex-1 min-h-0 overflow-auto divide-y divide-[var(--border)]">
          {bullets.map((bullet, idx) => (
            <li key={idx} className="px-5 py-4 space-y-2">
              <label className="block text-sm text-[var(--ink)]" htmlFor={`field-${idx}`}>
                <span className="font-mono-badge text-[10px] uppercase tracking-wider text-[var(--ink-faint)] mr-2">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                {bullet}
              </label>
              <textarea
                id={`field-${idx}`}
                value={fields[idx] ?? ""}
                onChange={(e) => update(idx, e.target.value)}
                disabled={disabled}
                rows={2}
                placeholder="Vul hier in…"
                className="w-full rounded-md bg-[var(--bg)] border border-[var(--border)] px-3 py-2 text-sm text-[var(--ink)] leading-relaxed placeholder:text-[var(--ink-faint)] resize-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2 disabled:opacity-50"
              />
            </li>
          ))}
      </ol>
    </article>
  );
}

function parseBulletFields(value: string, count: number): string[] {
  const fields: string[] = Array(count).fill("");
  if (!value) return fields;
  // Match each "N. body" block, preserving the body's whitespace verbatim
  // (only strip the single space that follows "N." per our serialisation
  // convention). Same reason as above: a keystroke-time round-trip must
  // not eat the space the user just pressed.
  const re = /(?:^|\n)(\d+)[.)] ?([\s\S]*?)(?=\n\d+[.)] |$)/g;
  let m: RegExpExecArray | null;
  let matched = false;
  while ((m = re.exec(value)) !== null) {
    const idx = Number(m[1]) - 1;
    const body = m[2] ?? "";
    if (idx >= 0 && idx < count) {
      fields[idx] = body;
      matched = true;
    }
  }
  if (!matched && value.length > 0) {
    fields[0] = value;
  }
  return fields;
}

function serialiseBulletFields(fields: string[]): string {
  // No .trim() — preserve user whitespace verbatim.
  return fields.map((t, i) => `${i + 1}. ${t ?? ""}`).join("\n");
}
